<template>
  <Card v-if="pending">
    <NamiLoading />
  </Card>
  <Card v-else-if="!computedProgresses.length">
    <NoResults :message="$t('site.org.id.slug.achievements.no_results')" />
  </Card>
  <div v-else-if="data" class="space-y-4">
    <AchievementProgressGroupCard
      v-for="grouped in computedProgresses"
      :progresses="grouped.progresses"
    />

    <NamiPagination
      v-model="page"
      :total-pages="Math.ceil((data?.meta.total ?? 0) / limit)"
      keep-route
    />
  </div>
</template>

<script setup lang="ts">
import {
  Achievement,
  type AchievementMetric,
  type AchievementProgressEntity,
  isAchievement,
  isExpanded,
  type OrganizationEntity,
} from "~/src/api";
import { useQuery } from "@tanstack/vue-query";

const { org } = defineProps<{
  org: OrganizationEntity;
}>();

const orgId = computed(() => org.id);

const { limit, offset, page } = useInternalPagination(1, 100);

const {
  data,
  isPending: pending,
  suspense,
} = useQuery({
  queryKey: ["achievement_progress", "collection", { orgId, offset, limit }],
  queryFn: () =>
    Achievement.getOrganizationAchievementProgresses(org.id, {
      includes: ["badge", "achievement"],
      order: { awardedAt: "desc" },
      limit: limit.value,
      offset: offset.value,
      // showAwarded: false,
    }),
});

onServerPrefetch(async () => {
  await suspense();
});

const filterHidden = (prog: AchievementProgressEntity) => {
  if (prog.attributes.targetReached) return true;
  const ach = prog.relationships.find(isAchievement);
  if (!ach || !isExpanded(ach)) return false;
  return !ach.attributes.isHidden;
};

const computedProgresses = computed(() => {
  const startingArr = data.value?.data.filter(filterHidden) ?? [];
  const groupedProgresses: {
    metric: AchievementMetric;
    progresses: AchievementProgressEntity[];
  }[] = [];

  startingArr.forEach((prog) => {
    const metric = prog.relationships.find(isAchievement)?.attributes?.metric;
    if (!metric) return;
    const foundGroup = groupedProgresses.find((g) => g.metric === metric);
    if (!foundGroup) {
      groupedProgresses.push({
        metric,
        progresses: [prog],
      });
    } else foundGroup.progresses.push(prog);
  });

  return groupedProgresses;
});
</script>
